/**
 * Result Page - Theatrical Progressive Reveal
 * 
 * SINGLE MODE (read from sessionStorage):
 * - 'verdict': Save as Verdict (requires sale price)
 * 
 * REVEAL SEQUENCE:
 * 0ms:    ThinkingBlock completes
 * 200ms:  TTCP scales in with bounce (the anchor)
 * 800ms:  Survival rate fades in
 * 1200ms: Delta badge punches in (the shock)
 * 1600ms: MirrorMoment reveals (the gut-punch)
 * 2000ms: Rest of content fades in
 * 
 * AI ANALYSIS: Generated at save time via triggerAnalysisAtSave → verdict.forensicRoast
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CorridorAnimated,
  TimeVsReturn
} from '@/components/ui';
import { ThinkingBlock } from '@/components/ui/ThinkingBlock';

import { TitleModal } from '@/components/ui/TitleModal';
import { TTCPReveal } from '@/components/ui/TTCPReveal';
import { DeltaReveal } from '@/components/ui/DeltaReveal';
import { MirrorMoment } from '@/components/ui/MirrorMoment';
import { AuthPromptModal } from '@/components/auth';
import { TrialBadge } from '@/components/subscription';
import { useAuth } from '@/contexts';
import { useDwellTime } from '@/hooks/useTelemetry';
import { useVerdicts, useSubscription, usePaywall } from '@/hooks';
import { Verdict, MEDIUMS, TIERS } from '@/types';
import { triggerAnalysisAtSave } from '@/lib/triggerAnalysis';
import { T } from '@/styles/tokens';

function formatCurrency(cents: number): string {
  const cappedCents = Math.min(Math.abs(cents), 1_000_000_000);
  const sign = cents < 0 ? '-' : '';

  return sign + new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cappedCents / 100);
}

type VerdictData = Omit<Verdict, 'id' | 'userId' | 'sold' | 'soldPrice' | 'soldDate' | 'createdAt' | 'updatedAt'>;

// Reveal timing constants (in ms) - Theatrical choreography
const REVEAL_TIMING = {
  LABEL: 300,           // "TRUE PRICE FLOOR" label fades in
  TTCP: 600,            // Count-up animation STARTS
  TTCP_LANDS: 2000,     // Count-up LANDS
  SURVIVAL_RATE: 800,   // Survival rate below floor
  DELTA: 2000,          // Delta badge appears after number lands
  VERDICT: 2400,        // Verdict sentence card slides up
  CORRIDOR: 2600,       // Corridor starts filling
  MIRROR_MOMENT: 2800,  // Mirror moment
  REST: 3200,           // Rest of content
  ACTIONS: 3600,        // Action buttons appear
};

export function ResultPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, profile } = useAuth();
  const { saveVerdict, markVerdictSold, isSaving } = useVerdicts({ watchForRefresh: false });
  const { isPremium, isTrialing, priceChecksUsed, priceChecksLimit } = useSubscription();
  const { openPaywall } = usePaywall();

  // Telemetry dwell tracking
  const reportDwellRef = useDwellTime('result', 'full_report');
  const corridorDwellRef = useDwellTime('result', 'price_corridor');
  const verdictDwellRef = useDwellTime('result', 'verdict');

  // Monetization: lifetime Price Check counter (3 free)
  const savedCount = priceChecksUsed;
  const canSaveFree = priceChecksUsed < priceChecksLimit;
  const savesRemaining = Math.max(0, priceChecksLimit - priceChecksUsed);

  // Read mode from sessionStorage (set by CalculatorForm)

  const [verdict, setVerdict] = useState<VerdictData | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [pieceTitle, setPieceTitle] = useState('Untitled');
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  const [showSavedAnimation, setShowSavedAnimation] = useState(false);
  const [activeCard, setActiveCard] = useState(0);
  
  // Sale price modal for verdict mode
  const [showSalePriceModal, setShowSalePriceModal] = useState(false);
  const [salePriceInput, setSalePriceInput] = useState('');
  const [pendingTitle, setPendingTitle] = useState('');
  
  const [savedVerdict, setSavedVerdict] = useState<Verdict | null>(null);

  // Discovery phase - shows after save with premium upsell
  const [phase, setPhase] = useState<'reveal' | 'actions' | 'discovery'>('reveal');

  const carouselRef = useRef<HTMLDivElement>(null);

  // DEV: URL param to force premium view (?premium=true)
  const isPremiumOverride = searchParams.get('premium') === 'true';
  const showPremiumContent = isPremium || isPremiumOverride;

  useEffect(() => {
    window.scrollTo(0, 0);

    const stored = sessionStorage.getItem('lastVerdict');
    if (stored) {
      try {
        const parsedVerdict = JSON.parse(stored);
        setVerdict(parsedVerdict);
        setIsAnalyzing(true);
      } catch (error) {
        console.error('ResultPage: Failed to parse verdict:', error);
        navigate('/app/calculate');
      }
    } else {
      console.error('ResultPage: No stored verdict found, navigating back to /calculate');
      navigate('/app/calculate');
    }
  }, [navigate]);

  // Track carousel scroll position for dot indicators
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      const scrollLeft = carousel.scrollLeft;
      const cardWidth = carousel.offsetWidth * 0.94;
      const newActive = Math.round(scrollLeft / cardWidth);
      setActiveCard(Math.min(Math.max(newActive, 0), 1));
    };

    carousel.addEventListener('scroll', handleScroll, { passive: true });
    return () => carousel.removeEventListener('scroll', handleScroll);
  }, [showResults]);

  // Handle thinking completion
  const handleAnalysisComplete = useCallback(() => {
    setIsAnalyzing(false);
    setTimeout(() => setShowResults(true), 150);
  }, []);

  // Handle save button click - opens title modal
  const handleSaveClick = () => {
    if (!isAuthenticated) {
      setShowAuthPrompt(true);
      return;
    }
    if (!isPremium && !canSaveFree) {
      openPaywall();
      return;
    }
    setShowTitleModal(true);
  };

  // Handle actual save after title is entered
  const handleSaveWithTitle = async (title: string) => {
    if (!verdict) return;

    // Save as verdict — need sale price first
    setPendingTitle(title);
    setShowTitleModal(false);
    setShowSalePriceModal(true);
  };
  
  // Handle verdict save with sale price (verdict mode only)
  const handleSaveVerdictWithSalePrice = async () => {
    if (!verdict || !pendingTitle) return;

    const priceInCents = Math.round(parseFloat(salePriceInput) * 100);
    if (isNaN(priceInCents) || priceInCents <= 0) return;

    // First, save the verdict
    const saved = await saveVerdict(
      { ...verdict.inputs, title: pendingTitle },
      verdict.calculations,
      verdict.flags,
    );

    if (saved) {
      // Then mark it as sold with the sale price
      await markVerdictSold(saved.id, priceInCents);

      // Fire-and-forget: trigger analysis generation so it's cached for re-open
      if (user) {
        triggerAnalysisAtSave({
          verdictId: saved.id,
          userId: user.uid,
          inputs: saved.inputs,
          calculations: saved.calculations,
          profile,
        });
      }

      sessionStorage.removeItem('lastVerdict');
      sessionStorage.removeItem('calculatorMode');
      const currentVersion = parseInt(sessionStorage.getItem('verdictsVersion') || '0', 10);
      sessionStorage.setItem('verdictsVersion', String(currentVersion + 1));
      setShowSalePriceModal(false);
      setSavedVerdict(saved);
      setShowSavedAnimation(true);
      // Transition to discovery phase after 600ms
      setTimeout(() => setPhase('discovery'), 600);
    } else {
      setShowSalePriceModal(false);
      alert('Unable to save. Try again.');
    }
  };

  // Scroll to specific card
  const scrollToCard = (index: number) => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    const cardWidth = carousel.offsetWidth * 0.94;
    carousel.scrollTo({ left: index * (cardWidth + 12), behavior: 'smooth' });
  };

  if (!verdict) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: T.cream }}>
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { inputs, calculations, flags } = verdict;
  const { ttcp, ceiling, area, density, laborValue, baseCost, scopeTax: scopeTaxAmount } = calculations;

  // Calculate Market Center (60% from floor toward ceiling)
  const marketCenter = Math.round(ttcp + (ceiling - ttcp) * 0.6);
  const mediumLabel = MEDIUMS[inputs.medium].label;
  const tierConfig = TIERS[inputs.tier];
  const tierLabel = TIERS[inputs.tier].label;

  const isOverDensity = density > 0.05;
  // survivalTarget is already in cents (monthlyBurn is in cents)
  const survivalTarget = inputs.monthlyCapacity > 0
    ? Math.round(inputs.monthlyBurn / inputs.monthlyCapacity)
    : 0;

  // Calculate effective hourly for this piece (gross, in cents)
  const effectiveHourly = inputs.gutPrice && inputs.hours > 0
    ? Math.round(inputs.gutPrice / inputs.hours)
    : null;

  const materialsCost = inputs.materials;

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col" style={{ background: T.cream }}>
      {/* Header */}
      <div className="pt-5 pb-4 flex-shrink-0">
        <header className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/app/calculate')}
              className="flex items-center justify-center w-10 h-10 -ml-2 rounded-lg hover:bg-charcoal-50 active:bg-charcoal-100 transition-all"
              aria-label="Back to calculator"
            >
              <svg className="w-5 h-5 text-charcoal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-charcoal-800">
              TruePrice Analysis
            </h1>
            {/* Share button removed - V2 feature */}
            <div className="w-10" />
          </div>
        </header>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-lg mx-auto pb-36 w-full">

        {/* Cards Container - Mobile: Carousel, Desktop: Stack */}
        <div className="relative px-4">
          <div
            ref={carouselRef}
            className={`flex md:flex-col gap-3 overflow-x-auto md:overflow-visible snap-x md:snap-none snap-mandatory scrollbar-hide transition-all duration-500 ${
              showResults ? 'opacity-100 translate-y-0' : 'opacity-40 translate-y-2'
            } md:px-0 -mx-4 md:mx-0 px-4`}
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {/* Card 1: Hero Card */}
            <div className="flex-shrink-0 w-[94%] md:w-full snap-start md:snap-align-none bg-white rounded-xl shadow-sm border border-charcoal-100 overflow-hidden">
              {/* Piece Context Header */}
              <div className="px-5 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-charcoal-700">
                      {pieceTitle}
                    </p>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-charcoal-400 mt-0.5">
                      {mediumLabel} · {inputs.width}×{inputs.height}" · {area.toLocaleString()} sq in
                    </p>
                  </div>
                  <button
                    onClick={() => setShowTitleModal(true)}
                    className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-charcoal-50 active:bg-charcoal-100 transition-all"
                    aria-label="Edit title"
                  >
                    <svg className="w-4 h-4 text-charcoal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Price Display - THEATRICAL REVEAL */}
              <div className="px-5 py-6">
                {/* YOUR FLOOR PRICE — the anchor */}
                <p className="text-[10px] font-medium uppercase tracking-wide text-charcoal-800 mb-2">
                  Your Floor Price
                </p>
                <TTCPReveal
                  value={ttcp}
                  isVisible={showResults}
                  delay={REVEAL_TIMING.TTCP}
                />

                {/* Survival rate */}
                <motion.p
                  initial={{ opacity: 0, y: 5 }}
                  animate={showResults ? { opacity: 1, y: 0 } : { opacity: 0, y: 5 }}
                  transition={{ delay: REVEAL_TIMING.SURVIVAL_RATE / 1000, duration: 0.3 }}
                  className="text-sm text-charcoal-500 mt-2"
                >
                  {formatCurrency(survivalTarget)}/hr to survive
                </motion.p>

                {/* Asking price — secondary */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={showResults ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ delay: REVEAL_TIMING.TTCP / 1000, duration: 0.4 }}
                  className="text-sm text-charcoal-600 mt-3"
                >
                  Your asking price: {formatCurrency(inputs.gutPrice ?? 0)}
                </motion.p>

                {/* Delta — explicit sentence */}
                {showResults && (
                  <div className="mt-4">
                    <DeltaReveal
                      gutPrice={inputs.gutPrice ?? 0}
                      ttcp={ttcp}
                      ceiling={ceiling}
                      isVisible={showResults}
                      delay={REVEAL_TIMING.DELTA}
                    />
                  </div>
                )}
              </div>

              {/* Floor Breakdown */}
              {showResults && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: REVEAL_TIMING.REST / 1000, duration: 0.4 }}
                >
                  <button
                    onClick={() => setBreakdownExpanded(!breakdownExpanded)}
                    className="w-full px-5 py-3 flex items-center justify-between hover:bg-charcoal-50/50 transition-colors"
                  >
                    <span className="text-[10px] font-medium uppercase tracking-wide text-charcoal-800">
                      Floor Breakdown
                    </span>
                    <svg
                      className={`w-4 h-4 text-charcoal-400 transition-transform duration-200 ${
                        breakdownExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      breakdownExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 pb-4 pt-1 space-y-2">
                      <div className="flex justify-between text-xs text-charcoal-400 pt-2 pb-2">
                        <span>Based on {formatCurrency(inputs.monthlyBurn)}/mo ÷ {inputs.monthlyCapacity} hrs</span>
                        <span>= {formatCurrency(survivalTarget)}/hr</span>
                      </div>

                      <div className="border-t border-charcoal-100 pt-2" />

                      <div className="flex justify-between text-sm">
                        <span className="text-charcoal-600">Time invested</span>
                        <span className="text-charcoal-500">{inputs.hours} hrs</span>
                      </div>
                      
                      {tierConfig.multiplier !== 1.0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-charcoal-600">Complexity ({tierLabel})</span>
                          <span className="text-charcoal-500">×{tierConfig.multiplier}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-charcoal-700">Labor</span>
                        <span className="text-charcoal-700">{formatCurrency(laborValue)}</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-charcoal-600">Materials</span>
                        <span className="text-charcoal-700">{formatCurrency(materialsCost)}</span>
                      </div>
                      
                      <div className="border-t border-charcoal-100 my-1" />
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-charcoal-600">Subtotal</span>
                        <span className="text-charcoal-700">{formatCurrency(baseCost)}</span>
                      </div>
                      
                      {tierConfig.scopeTax > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-charcoal-600">Risk buffer ({(tierConfig.scopeTax * 100).toFixed(0)}%)</span>
                          <span className="text-charcoal-700">{formatCurrency(scopeTaxAmount)}</span>
                        </div>
                      )}
                      
                      <div className="border-t border-charcoal-200 pt-2 mt-1">
                        <div className="flex justify-between text-sm font-semibold">
                          <span className="text-charcoal-800">Floor</span>
                          <span className="text-teal-600">{formatCurrency(ttcp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Card 2: Price Position — Animated Corridor */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={showResults ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ delay: REVEAL_TIMING.VERDICT / 1000, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="flex-shrink-0 w-[94%] snap-start bg-white rounded-[18px] shadow-sm border border-charcoal-100 overflow-hidden flex flex-col"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.02), 0 8px 24px rgba(0,0,0,0.03)' }}
            >
              {/* Header: label left, medium+dims right */}
              <div className="px-5 py-4 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#c5c0b8]">
                  Price Position
                </p>
                <span className="text-[10px] font-semibold text-[#c5c0b8] tracking-[0.3px]">
                  {mediumLabel} · {inputs.width}" × {inputs.height}"
                </span>
              </div>

              <div className="px-5 pb-5">
                <CorridorAnimated
                  price={inputs.gutPrice ?? ttcp}
                  floor={ttcp}
                  ceiling={ceiling}
                  medium={mediumLabel}
                  width={inputs.width}
                  height={inputs.height}
                  delay={REVEAL_TIMING.CORRIDOR}
                />

                {/* Disclaimer */}
                <p className="text-[10px] text-[#d5d0c8] mt-4 leading-relaxed italic">
                  Based on comparable listings at this size and medium.
                </p>
              </div>
            </motion.div>

            <div className="flex-shrink-0 w-1" />
          </div>

          {/* Dot Indicators - Mobile Only */}
          {showResults && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: REVEAL_TIMING.REST / 1000, duration: 0.3 }}
              className="flex md:hidden justify-center gap-1.5 mt-3"
            >
              {[0, 1].map((index) => (
                <button
                  key={index}
                  onClick={() => scrollToCard(index)}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    activeCard === index 
                      ? 'bg-charcoal-500 w-4' 
                      : 'bg-charcoal-200 w-1.5 hover:bg-charcoal-300'
                  }`}
                  aria-label={`Go to card ${index + 1}`}
                />
              ))}
            </motion.div>
          )}
        </div>

        {/* Rest of content */}
        <div className="px-4 space-y-4">
          {/* Trial badge */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={showResults ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: REVEAL_TIMING.REST / 1000, duration: 0.3 }}
            className="flex justify-center py-1 min-h-[32px]"
          >
            {(isPremium || isTrialing) && <TrialBadge />}
          </motion.div>

          {isPremiumOverride && !isPremium && (
            <div className="px-2 py-1 bg-purple-100 border border-purple-300 rounded text-xs text-purple-700 text-center">
              Premium Preview Mode
            </div>
          )}

          {/* Mirror Moment - THE GUT PUNCH */}
          {effectiveHourly !== null && showResults && (
            <MirrorMoment
              effectiveHourly={effectiveHourly}
              survivalTarget={survivalTarget}
              isVisible={showResults}
              delay={REVEAL_TIMING.MIRROR_MOMENT}
            />
          )}

          {/* Your Numbers — Key Metrics */}
          {showResults && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (REVEAL_TIMING.MIRROR_MOMENT + 200) / 1000, duration: 0.4 }}
              className="grid grid-cols-3 gap-3"
            >
              {/* Effective Hourly */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-charcoal-100 text-center">
                <p className="text-[9px] font-medium uppercase tracking-wide text-charcoal-500 mb-1">
                  Hourly Rate
                </p>
                <p className="text-base font-bold text-charcoal-800">
                  {effectiveHourly !== null ? formatCurrency(effectiveHourly) : '—'}
                </p>
                <p className="text-[10px] text-charcoal-400">/hr</p>
              </div>

              {/* Density */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-charcoal-100 text-center">
                <p className="text-[9px] font-medium uppercase tracking-wide text-charcoal-500 mb-1">
                  Density
                </p>
                <p className="text-base font-bold text-charcoal-800">
                  {density.toFixed(3)}
                </p>
                <p className="text-[10px] text-charcoal-400">h/in²</p>
              </div>

              {/* Sustainability */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-charcoal-100 text-center">
                <p className="text-[9px] font-medium uppercase tracking-wide text-charcoal-500 mb-1">
                  Sustainability
                </p>
                <p className={`text-base font-bold ${
                  calculations.sustainabilityRatio !== null && calculations.sustainabilityRatio >= 1
                    ? 'text-emerald-600'
                    : 'text-red-600'
                }`}>
                  {calculations.sustainabilityRatio !== null
                    ? `${(calculations.sustainabilityRatio * 100).toFixed(0)}%`
                    : '—'}
                </p>
              </div>
            </motion.div>
          )}

          {/* Thinking Block */}
          <ThinkingBlock
            inputs={inputs}
            calculations={calculations}
            flags={flags}
            isAnalyzing={isAnalyzing}
            onAnalysisComplete={handleAnalysisComplete}
          />

          {/* Time vs Return */}
          {showResults && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (REVEAL_TIMING.REST + 200) / 1000, duration: 0.4 }}
              className="bg-white rounded-xl shadow-sm border border-charcoal-100 p-5"
            >
              <TimeVsReturn
                totalMonthlyHours={inputs.monthlyCapacity}
                monthlyOverhead={inputs.monthlyBurn}
                hoursSpent={inputs.hours}
                price={inputs.gutPrice ?? 0}
              />
            </motion.div>
          )}

          {/* Flags */}
          {showResults && (flags.isSpeedVeto || flags.smallWorkLogicApplied || flags.detailPremiumApplied ||
            flags.masterBoostApplied || flags.materialExceptionApplied || flags.densityRelaxerApplied || isOverDensity) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: (REVEAL_TIMING.REST + 400) / 1000, duration: 0.3 }}
              className="flex flex-wrap gap-1.5"
            >
              {flags.smallWorkLogicApplied && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-charcoal-50 text-charcoal-600 text-xs font-medium rounded-lg border border-charcoal-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-charcoal-400" />
                  Small work rules
                </span>
              )}
              {flags.detailPremiumApplied && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Detail premium +{((calculations.detailPremium - 1) * 100).toFixed(0)}%
                </span>
              )}
              {flags.masterBoostApplied && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Master rate boost
                </span>
              )}
              {flags.materialExceptionApplied && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-charcoal-50 text-charcoal-600 text-xs font-medium rounded-lg border border-charcoal-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-charcoal-400" />
                  Material-intensive
                </span>
              )}
              {flags.densityRelaxerApplied && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-charcoal-50 text-charcoal-600 text-xs font-medium rounded-lg border border-charcoal-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-charcoal-400" />
                  Paper medium
                </span>
              )}
              {isOverDensity && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  High labor intensity
                </span>
              )}
            </motion.div>
          )}

          {/* Disclaimer */}
          {showResults && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: (REVEAL_TIMING.REST + 600) / 1000, duration: 0.3 }}
              className="pt-4 pb-2"
            >
              <p className="text-[11px] text-charcoal-400 text-center leading-relaxed">
                TruePrice calculates your cost floor, not market value. This is a decision aid, not professional advice.
              </p>
            </motion.div>
          )}
        </div>
      </main>

      {/* Fixed bottom buttons */}
      {showResults && !showSavedAnimation && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: (REVEAL_TIMING.REST + 300) / 1000, duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-14 md:bottom-0 inset-x-0 z-30"
        >
          <div className="bg-white border-t border-charcoal-100">
            <div className="max-w-lg mx-auto px-4 py-2.5">
              <button
                onClick={() => {
                  if (verdict?.inputs) {
                    localStorage.setItem('trueprice_form_state', JSON.stringify({
                      medium: inputs.medium,
                      width: inputs.width,
                      height: inputs.height,
                      hours: inputs.hours,
                      materials: inputs.materials,
                      tier: inputs.tier,
                    }));
                  }
                  navigate('/app/calculate');
                }}
                className="w-full text-center text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors"
              >
                New
              </button>
            </div>
          </div>

          {/* Save count indicator for free users */}
          {isAuthenticated && !isPremium && (
            <p className="text-xs text-charcoal-500 text-center py-1">
              {savesRemaining > 0
                ? `${savesRemaining} of ${priceChecksLimit} free Price Check${savesRemaining === 1 ? '' : 's'} remaining`
                : "You've used your free Price Checks. Subscribe to save unlimited."}
            </p>
          )}

          <button
            onClick={handleSaveClick}
            disabled={isSaving}
            className={`w-full min-h-[70px] pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-base font-semibold transition-all ${
              isSaving
                ? 'bg-teal-500/80 text-white/80 cursor-wait'
                : 'bg-teal-500 text-white active:bg-teal-600'
            }`}
            aria-label="save this piece"
          >
            {isSaving
              ? 'Saving...'
              : 'Save & Name This Piece'
            }
          </button>
        </motion.div>
      )}

      {/* Saved animation with discovery phase */}
      {showSavedAnimation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-40 bg-white flex flex-col items-center justify-center px-6 overflow-y-auto"
        >
          {/* Initial save confirmation - fades to discovery */}
          {phase !== 'discovery' && (
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.1, duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
              className="flex flex-col items-center text-center"
            >
              {/* Checkmark */}
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                'bg-teal-100'
              }`}>
                <svg className={`w-8 h-8 ${'text-teal-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-xl font-semibold text-charcoal-800 mb-2">
                {'Piece saved'}
              </h2>
              <p className="text-sm text-charcoal-500 mb-8 max-w-xs">
                Preparing your analysis...
              </p>
            </motion.div>
          )}

          {/* Discovery Phase - Spotify-style post-action reveal */}
          {phase === 'discovery' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-sm"
            >
              {/* Discovery Card */}
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: 20,
                  padding: '24px 20px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.02), 0 12px 32px rgba(0,0,0,0.05)',
                  marginBottom: 12,
                }}
              >
                {/* Discovery header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div
                    className="animate-pulse-glow"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, rgba(61,171,165,0.12), rgba(61,171,165,0.06))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ fontSize: 14 }}>✦</span>
                  </div>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: T.navy, margin: 0, lineHeight: 1.3 }}>
                      We found 4 insights for this piece
                    </p>
                    <p style={{ fontSize: 11, color: T.textMuted, margin: 0, marginTop: 2 }}>
                      Based on your pricing data
                    </p>
                  </div>
                </div>

                {/* Insight rows — staggered fade-in */}
                {[
                  { text: `Your materials cost $${((inputs.materials / 100) / area).toFixed(2)} per square inch`, delay: 150 },
                  { text: `Labor efficiency is ${effectiveHourly && survivalTarget ? Math.round((effectiveHourly / survivalTarget) * 100) : 100}% of target rate`, delay: 300 },
                  { text: `This size typically commands ${area > 500 ? '15-25%' : '5-10%'} premium`, delay: 450 },
                  { text: `At this price, runway is ${inputs.gutPrice && inputs.monthlyBurn ? ((inputs.gutPrice / inputs.monthlyBurn) * 30).toFixed(1) : '—'} days`, delay: 600 },
                ].map((insight, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: insight.delay / 1000, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 10,
                      backgroundColor: i % 2 === 0 ? 'rgba(0,0,0,0.015)' : 'transparent',
                      marginBottom: 4,
                    }}
                  >
                    <div style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      backgroundColor: T.primary,
                      opacity: 0.5,
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.4 }}>
                      {insight.text}
                    </span>
                  </motion.div>
                ))}

                {/* Upgrade CTA — for non-premium users */}
                {!showPremiumContent && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f2efea' }}
                  >
                    <button
                      onClick={openPaywall}
                      style={{
                        width: '100%',
                        height: 48,
                        borderRadius: 14,
                        border: 'none',
                        background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                        color: 'white',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 4px 16px rgba(61,171,165,0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      <span>Unlock full analysis</span>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 8,
                        backgroundColor: 'rgba(255,255,255,0.12)',
                      }}>7 days free</span>
                    </button>
                    <p style={{ textAlign: 'center', fontSize: 11, color: T.textMuted, marginTop: 8 }}>
                      $12.99/mo after trial · Cancel anytime
                    </p>
                  </motion.div>
                )}
              </div>

              {/* Post-save action buttons */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'flex', gap: 8 }}
              >
                <button
                  onClick={() => navigate('/app/calculate')}
                  style={{
                    flex: 1,
                    height: 46,
                    borderRadius: 14,
                    border: '1.5px solid #eae7e2',
                    backgroundColor: 'white',
                    fontSize: 13,
                    fontWeight: 600,
                    color: T.textSecondary,
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  }}
                >
                  Price Another
                </button>
              </motion.div>

              {/* Go to Portfolio link */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.3 }}
                onClick={() => {
                  navigate('/app/portfolio');
                  window.scrollTo(0, 0);
                }}
                style={{
                  width: '100%',
                  marginTop: 12,
                  padding: '12px 0',
                  background: 'none',
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.primary,
                  cursor: 'pointer',
                }}
              >
                Go to Portfolio →
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Brand Footer */}
      <div style={{ textAlign: 'center', padding: '16px 0 8px', borderTop: '1px solid #E5E1D8' }}>
        <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.05em' }}>
          TruePrice · trueprice.app
        </span>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(61,171,165,0.15); }
          50% { box-shadow: 0 0 0 6px rgba(61,171,165,0.05); }
        }

        .animate-pulse-glow {
          animation: pulseGlow 2s ease infinite;
        }
      `}</style>

      <TitleModal
        isOpen={showTitleModal}
        onCancel={() => setShowTitleModal(false)}
        onSave={handleSaveWithTitle}
        isSaving={isSaving}
      />

      <AuthPromptModal
        isOpen={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        action={"save this piece"}
      />

      {/* Sale Price Modal - Verdict mode only */}
      {showSalePriceModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-lg bg-white rounded-t-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
          >
            <h2 className="text-lg font-semibold text-charcoal-800 mb-2">What did you sell it for?</h2>
            <p className="text-sm text-charcoal-500 mb-4">
              Enter the final sale price to complete your audit.
            </p>
            <div className="relative mb-6">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-400">$</span>
              <input
                type="number"
                inputMode="decimal"
                value={salePriceInput}
                onChange={(e) => setSalePriceInput(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 border border-charcoal-200 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSalePriceModal(false);
                  setSalePriceInput('');
                }}
                className="flex-1 py-3 border border-charcoal-200 text-charcoal-600 font-medium rounded-xl hover:bg-charcoal-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVerdictWithSalePrice}
                disabled={isSaving || !salePriceInput}
                className="flex-1 py-3 bg-emerald-500 text-white font-medium rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? 'Saving...' : 'Save Audit'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

    </div>
  );
}

export default ResultPage;
